import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MenuSheet, type SheetSize } from './MenuSheet'
import { printMenu } from '../../utils/printMode'
import { useFitToPage } from '../../hooks/useFitToPage'
import { clearLogo, fileToLogo, loadLogo, saveLogo } from '../../services/menuLogo'
import type { Category, Product } from '../../types'
import './MenuSheet.css'
import './MenuPrintDialog.css'

interface Props {
  /** Scopes the stored logo, so two yurts on one device keep their own. */
  tenantId: string
  tenantName: string
  categories: Category[]
  products: Product[]
  onClose: () => void
}

/** Roughly how wide the preview pane is, so the sheet is scaled to fit it. */
const SHEET_MM: Record<SheetSize, number> = { a5: 148, a4: 210 }
const MM_PER_PX = 96 / 25.4

/**
 * Builds a menu card from the catalogue and sends it to a printer.
 *
 * Everything is ticked on open. The owner is printing *their* menu, so the
 * common case is "all of it" and the work is in taking things out, a
 * seasonal item, a category the stall isn't running today.
 */
export function MenuPrintDialog({
  tenantId,
  tenantName,
  categories,
  products,
  onClose,
}: Props) {
  const [size, setSize] = useState<SheetSize>('a5')
  /**
   * One column on A5, two on A4.
   *
   * 148mm split in two leaves about 65mm a column, which "Tavuk İskender (1.5
   * Porsiyon)" cannot share with its price without wrapping every other line.
   * Changing the paper moves this with it, and picking a column count by hand
   * afterwards still overrides it.
   */
  const [columns, setColumns] = useState<1 | 2>(1)
  const [title, setTitle] = useState(tenantName.toUpperCase())
  const [hours, setHours] = useState('')
  const [showPrices, setShowPrices] = useState(true)
  const [showDescriptions, setShowDescriptions] = useState(true)

  /** Remembered per device, see menuLogo.ts for why it is not in Firestore. */
  const [logo, setLogo] = useState<string | null>(() => loadLogo(tenantId))
  const [logoError, setLogoError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const pickLogo = async (file: File | undefined) => {
    if (!file) return
    setLogoError(null)
    try {
      const uri = await fileToLogo(file)
      saveLogo(tenantId, uri)
      setLogo(uri)
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : 'Görsel yüklenemedi.')
    }
  }

  const removeLogo = () => {
    clearLogo(tenantId)
    setLogo(null)
    setLogoError(null)
    // Without this the same file picked again fires no change event.
    if (fileRef.current) fileRef.current.value = ''
  }

  /**
   * What is *left out*, rather than what is in.
   *
   * Holding the exclusions means a product added to the menu while this dialog
   * is open arrives ticked, which matches "print my menu", an opt-out list
   * would silently drop it.
   */
  const [excluded, setExcluded] = useState<Set<string>>(new Set())

  const sorted = useMemo(
    () => [...categories].filter((c) => c.active).sort((a, b) => a.sortOrder - b.sortOrder),
    [categories],
  )

  /** Sold-out items stay: they are on the menu, just not in the pan today. */
  const listable = useMemo(
    () => products.filter((p) => p.active).sort((a, b) => a.sortOrder - b.sortOrder),
    [products],
  )

  const chosen = useMemo(
    () => listable.filter((p) => !excluded.has(p.id)),
    [listable, excluded],
  )

  const toggle = (id: string) =>
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  /** Whole category in or out in one tap, the usual bulk edit. */
  const toggleCategory = (categoryId: string) => {
    const ids = listable.filter((p) => p.categoryId === categoryId).map((p) => p.id)
    const allOut = ids.every((id) => excluded.has(id))
    setExcluded((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (allOut) next.delete(id)
        else next.add(id)
      }
      return next
    })
  }

  // The sheet is drawn at its real millimetre size and scaled down to fit,
  // rather than being laid out smaller: what is on screen is then the same
  // arrangement that comes out of the printer, line breaks included.
  //
  // Measured from the pane itself rather than from window.innerWidth: the pane
  // is one cell of a grid that reflows at 860px, so its width is not a fixed
  // fraction of the window and guessing it left the A4 preview clipped.
  const [paneWidth, setPaneWidth] = useState(0)
  const paneRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = paneRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      setPaneWidth(entry.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const zoom = useMemo(() => {
    if (paneWidth === 0) return 1
    return Math.min(1, paneWidth / MM_PER_PX / SHEET_MM[size])
  }, [paneWidth, size])
  const zoomRef = useRef(1)
  zoomRef.current = zoom

  /**
   * Where the portalled card should sit on screen.
   *
   * The card lives at <body> so that printing can hide the app outright (see
   * the portal below), which means it no longer sits inside the preview pane
   * on its own. This tracks the empty stage left behind in the pane and places
   * the card over it, so the preview looks exactly as it did.
   *
   * Tracked rather than computed once: the pane scrolls, the dialog reflows at
   * 860px, and the stage changes size whenever the paper does.
   */
  const stageRef = useRef<HTMLDivElement>(null)
  const [spot, setSpot] = useState<{ top: number; left: number; clip?: string } | null>(null)

  useLayoutEffect(() => {
    const el = stageRef.current
    if (!el) return

    const place = () => {
      const r = el.getBoundingClientRect()
      // Sayfa ayrı katmanda: önizleme kutusu kaydırılınca kutunun dışına
      // (başlığın, düğmelerin üstüne) taşmasın diye kutunun sınırına kırpılıyor.
      // Kırpma sayfanın kendi (ölçeklenmemiş) ölçüsünde, o yüzden zoom'a bölünüyor.
      const pane = paneRef.current?.getBoundingClientRect()
      let clip: string | undefined
      if (pane) {
        const z = zoomRef.current || 1
        const t = Math.max(0, pane.top - r.top) / z
        const l = Math.max(0, pane.left - r.left) / z
        const b = Math.max(0, r.bottom - pane.bottom) / z
        const rt = Math.max(0, r.right - pane.right) / z
        clip = `inset(${t}px ${rt}px ${b}px ${l}px)`
      }
      setSpot((cur) =>
        cur && cur.top === r.top && cur.left === r.left && cur.clip === clip
          ? cur
          : { top: r.top, left: r.left, clip },
      )
    }

    place()
    const ro = new ResizeObserver(place)
    ro.observe(el)
    // Kutu boyu değişmeden yeri değişebiliyor: başlık altındaki "sığmıyor"
    // uyarısı gelip gidince ya da pencere açılırken kayarak gelince. Sol
    // sütun ve pencerenin kendisi de izleniyor, açılış hareketi boyunca da
    // her karede ölçülüyor; yoksa sayfa eski yerinde kalıp düğmelerin
    // üstüne biniyordu.
    const card = el.closest('.mprint')
    if (card) ro.observe(card)
    const side = card?.querySelector('.mprint__side')
    if (side) ro.observe(side)
    let frames = 0
    let raf = 0
    const tick = () => {
      place()
      if (++frames < 40) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    // The pane scrolls under the card, and the window can be resized around it.
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [size, columns])

  /**
   * Type scale that keeps the card on one page.
   *
   * The measurement is taken from the preview, which is the same DOM the
   * printer gets, so what fits on screen is what fits on paper. `zoom` is a
   * transform and does not affect it.
   */
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null)
  const { fit, atFloor } = useFitToPage(bodyEl, [
    size,
    columns,
    chosen.length,
    showPrices,
    showDescriptions,
    Boolean(logo),
    title,
    hours,
  ])

  return (
    <div className="dlg" role="dialog" aria-modal="true">
      <div className="dlg__card mprint">
        {/* Kapat ve Yazdır sağ üstte: form uzun, en alta inmeden yazdırılsın. */}
        <div className="mprint__head">
          <h2>Menü Yazdır</h2>
          <div className="mprint__head-actions">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Kapat
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={chosen.length === 0}
              onClick={() => printMenu(size)}
            >
              Yazdır
            </button>
          </div>
        </div>
        {/* Yeri hep ayrılmış: uyarı gelip gidince önizleme kutusu kaymasın
            (sayfa kutunun üstüne ayrıca konumlanıyor, kayınca taşıyordu). */}
        <p className={`mprint__status ${atFloor ? 'mprint__warn' : 'mprint__fit'}`}>
          {atFloor
            ? 'Tek sayfaya sığmıyor. Ürün çıkarın, A4 seçin veya açıklamaları kapatın.'
            : fit < 1
              ? `Tek sayfaya sığdırıldı: %${Math.round(fit * 100)}`
              : '\u00a0'}
        </p>

        <div className="mprint__body">
          <div className="mprint__side">
            <div className="mprint__row">
              <label className="dlg__field">
                Başlık
                <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={24} />
              </label>
              {/* Alt yazı ve alt not (slogan) kaldırıldı (kullanıcı, 13 Eki
                  2026): başlığın yanında yalnız servis saatleri. */}
              <label className="dlg__field">
                Servis saatleri
                <input
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  maxLength={16}
                  placeholder="15:00-22:30"
                  inputMode="numeric"
                />
              </label>
            </div>

            <div className="mprint__opts">
              <div className="mprint__seg" role="group" aria-label="Kağıt boyutu">
                <button
                  type="button"
                  className={`mprint__segbtn ${size === 'a5' ? 'on' : ''}`}
                  onClick={() => {
                    setSize('a5')
                    setColumns(1)
                  }}
                >
                  A5
                </button>
                <button
                  type="button"
                  className={`mprint__segbtn ${size === 'a4' ? 'on' : ''}`}
                  onClick={() => {
                    setSize('a4')
                    setColumns(2)
                  }}
                >
                  A4
                </button>
              </div>

              <div className="mprint__seg" role="group" aria-label="Sütun sayısı">
                <button
                  type="button"
                  className={`mprint__segbtn ${columns === 1 ? 'on' : ''}`}
                  onClick={() => setColumns(1)}
                >
                  Tek sütun
                </button>
                <button
                  type="button"
                  className={`mprint__segbtn ${columns === 2 ? 'on' : ''}`}
                  onClick={() => setColumns(2)}
                >
                  Çift sütun
                </button>
              </div>
            </div>

            <div className="mprint__logo">
              {logo ? (
                <img className="mprint__logo-img" src={logo} alt="Seçili logo" />
              ) : (
                <span className="mprint__logo-empty">Logo yok</span>
              )}
              <div className="mprint__logo-acts">
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => fileRef.current?.click()}
                >
                  {logo ? 'Değiştir' : 'Logo seç'}
                </button>
                {logo && (
                  <button type="button" className="btn btn--ghost btn--sm" onClick={removeLogo}>
                    Kaldır
                  </button>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => void pickLogo(e.target.files?.[0])}
              />
            </div>
            {logoError && <p className="mprint__warn">{logoError}</p>}

            <div className="mprint__opts">
              <label className={`opt ${showPrices ? 'on' : ''}`}>
                <input
                  type="checkbox"
                  checked={showPrices}
                  onChange={() => setShowPrices((v) => !v)}
                />
                Fiyatlar
              </label>
              <label className={`opt ${showDescriptions ? 'on' : ''}`}>
                <input
                  type="checkbox"
                  checked={showDescriptions}
                  onChange={() => setShowDescriptions((v) => !v)}
                />
                Açıklamalar
              </label>
            </div>

            <p className="mprint__hint">
              Tüm ürünler seçili. Menüde <strong>istemediklerinizin</strong> tikini kaldırın.
            </p>

            <div className="mprint__picker">
              {sorted.map((c) => {
                const items = listable.filter((p) => p.categoryId === c.id)
                if (items.length === 0) return null
                const inCount = items.filter((p) => !excluded.has(p.id)).length
                return (
                  <section key={c.id} className="mprint__group">
                    <button
                      type="button"
                      className="mprint__grouphead"
                      onClick={() => toggleCategory(c.id)}
                    >
                      <span>{c.name}</span>
                      <span className="mprint__count">
                        {inCount}/{items.length}
                      </span>
                    </button>
                    {items.map((p) => (
                      <label
                        key={p.id}
                        className={`opt opt--wide ${!excluded.has(p.id) ? 'on' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={!excluded.has(p.id)}
                          onChange={() => toggle(p.id)}
                        />
                        <span className="mprint__pname">{p.name}</span>
                        {p.soldOut && <span className="mprint__tag">bitti</span>}
                      </label>
                    ))}
                  </section>
                )
              })}
            </div>
          </div>

          <div className="mprint__preview" ref={paneRef}>
            <div
              className="mprint__stage"
              ref={stageRef}
              style={{
                // A transform leaves the element's *unscaled* box in the
                // layout, so without this the pane reserves room for a full-size
                // A4 and scrolls a long way past the visible card.
                width: `${SHEET_MM[size] * MM_PER_PX * zoom}px`,
                height: `${(size === 'a5' ? 210 : 297) * MM_PER_PX * zoom}px`,
              }}
            >
              {/*
                Portalled to <body> rather than rendered here.

                Printing needs the app removed from the document, not just
                hidden: `visibility: hidden` leaves the app's ~3000px of height
                in place, and a fixed A5 page cut that into four sheets with the
                card truncated on the first. Only `display: none` reclaims the
                height, and that would take the card down with it if the card
                lived inside the dialog.

                Out at <body> the card survives the app being hidden. On screen
                it is placed back over this stage by CSS, so the preview looks
                unchanged and the fitter still measures the very DOM that gets
                printed.
              */}
              {createPortal(
                <MenuSheet
                  title={title}
                  hours={hours}
                  size={size}
                  categories={sorted}
                  products={chosen}
                  columns={columns}
                  showPrices={showPrices}
                  showDescriptions={showDescriptions}
                  logo={logo}
                  preview
                  zoom={zoom}
                  spot={spot}
                  fit={fit}
                  bodyRef={setBodyEl}
                />,
                document.body,
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
